import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import CountUp from 'react-countup';
import { TrendingUpIcon, CoinsIcon, PercentIcon, CalendarIcon, HomeIcon, BuildingIcon, ArrowRightIcon } from 'lucide-react';
export function InvestmentCalculator() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [activeTab, setActiveTab] = useState('rental');
  const [rentalRate, setRentalRate] = useState(12);
  const [pricePerSqm, setPricePerSqm] = useState(3900);
  // Constants
  const investmentAmount = 3612460;
  const totalArea = 2500;
  const vatRate = 0.19;
  // Rental Calculations
  const monthlyRentalIncome = totalArea * rentalRate;
  const annualRentalIncome = monthlyRentalIncome * 12;
  const threeYearRentalIncome = annualRentalIncome * 3;
  const tenYearRentalIncome = annualRentalIncome * 10;
  const rentalROI = annualRentalIncome / investmentAmount * 100;
  // Resale Calculations
  const pricePerSqmWithVAT = pricePerSqm * (1 + vatRate);
  const totalResaleValue = totalArea * pricePerSqmWithVAT;
  const resaleProfit = totalResaleValue - investmentAmount;
  const resaleROI = resaleProfit / investmentAmount * 100;
  const tabs = [{
    id: 'rental',
    label: 'Randament Închiriere',
    icon: HomeIcon
  }, {
    id: 'resale',
    label: 'Randament Revânzare',
    icon: BuildingIcon
  }];
  const tabContentVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    },
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.3
      }
    }
  };
  return <section className="py-12 md:py-20 px-4 md:px-6 lg:px-16 bg-white" ref={ref} data-id="element-175">
      <div className="max-w-7xl mx-auto" data-id="element-176">
        <motion.h2 className="text-2xl md:text-3xl lg:text-5xl font-bold text-[#132c53] text-center mb-3 md:mb-4 px-4" initial={{
        y: -50,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8
      }} data-id="element-177">
          Calculator <span className="text-[#357beb]" data-id="element-178">Investiție</span>
        </motion.h2>
        <motion.p className="text-gray-700 text-center mb-8 md:mb-12 max-w-3xl mx-auto text-base md:text-lg px-4" initial={{
        y: 30,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.2
      }} data-id="element-179">
          Calculează randamentul investiției tale în funcție de strategia
          aleasă: închiriere pe termen lung sau revânzare.
        </motion.p>
        {/* Investment Amount Banner */}
        <motion.div className="bg-gradient-to-br from-[#132c53] to-[#1a3a6b] p-6 md:p-8 rounded-xl md:rounded-2xl shadow-xl mb-8 md:mb-12" initial={{
        scale: 0.9,
        opacity: 0
      }} animate={inView ? {
        scale: 1,
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.3
      }} data-id="element-180">
          <div className="flex items-center justify-center gap-3 md:gap-4 mb-2" data-id="element-181">
            <CoinsIcon className="w-6 h-6 md:w-8 md:h-8 text-white" data-id="element-182" />
            <p className="text-white text-xs md:text-sm font-semibold uppercase tracking-wider" data-id="element-183">
              Investiție Totală
            </p>
          </div>
          <p className="text-white text-3xl md:text-4xl lg:text-5xl font-bold text-center" data-id="element-184">
            {investmentAmount.toLocaleString()} €
          </p>
          <p className="text-gray-300 text-center mt-2 text-sm md:text-base" data-id="element-185">
            Suprafață: {totalArea.toLocaleString()} mpc
          </p>
        </motion.div>
        {/* Tabs */}
        <motion.div className="bg-white rounded-xl shadow-lg overflow-hidden" initial={{
        y: 50,
        opacity: 0
      }} animate={inView ? {
        y: 0,
        opacity: 1
      } : {}} transition={{
        duration: 0.8,
        delay: 0.4
      }} data-id="element-186">
          <div className="flex border-b border-gray-200" data-id="element-187">
            {tabs.map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className="flex-1 py-4 md:py-5 px-3 md:px-6 text-xs md:text-base font-medium transition-all relative hover:bg-gray-50 flex flex-col md:flex-row items-center justify-center gap-1 md:gap-2 touch-manipulation" style={{
            color: activeTab === tab.id ? '#357beb' : '#6b7280'
          }} data-id="element-188">
                <tab.icon className="w-5 h-5 md:w-6 md:h-6" data-id="element-189" />
                <span className="text-center leading-tight" data-id="element-190">{tab.label}</span>
                {activeTab === tab.id && <motion.div layoutId="activeInvestmentTab" className="absolute bottom-0 left-0 right-0 h-1 bg-[#357beb]" transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-191" />}
              </button>)}
          </div>
          {/* Tab Content */}
          <div className="p-4 md:p-8 lg:p-12" data-id="element-192">
            <AnimatePresence mode="wait" data-id="element-193">
              {/* Rental Tab */}
              {activeTab === 'rental' && <motion.div key="rental" variants={tabContentVariants} initial="hidden" animate="visible" exit="exit" data-id="element-194">
                  <div className="max-w-4xl mx-auto" data-id="element-195">
                    <h3 className="text-xl md:text-2xl font-bold text-[#132c53] mb-6 md:mb-8 text-center px-4" data-id="element-196">
                      Calculează randamentul din închiriere
                    </h3>
                    {/* Rental Rate Slider */}
                    <div className="bg-gray-50 p-6 md:p-8 rounded-xl mb-6 md:mb-8" data-id="element-197">
                      <label className="block text-center text-base md:text-lg font-bold text-[#132c53] mb-4" data-id="element-198">
                        Chirie medie (€/mp/lună)
                      </label>
                      <div className="text-center mb-6" data-id="element-199">
                        <span className="text-4xl md:text-5xl font-bold text-[#357beb]" data-id="element-200">
                          {rentalRate}
                        </span>
                        <span className="text-xl md:text-2xl font-bold text-[#357beb] ml-2" data-id="element-201">
                          € +TVA
                        </span>
                      </div>
                      <input type="range" min="12" max="25" step="0.5" value={rentalRate} onChange={e => setRentalRate(Number(e.target.value))} className="w-full h-4 md:h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#357beb] touch-manipulation" style={{
                    WebkitAppearance: 'none'
                  }} data-id="element-202" />
                      <div className="flex justify-between text-xs md:text-sm text-gray-500 mt-2 px-1" data-id="element-203">
                        <span data-id="element-204">12 € +TVA</span>
                        <span data-id="element-205">25 € +TVA</span>
                      </div>
                      <div className="mt-6 bg-white border-2 border-[#357beb] rounded-lg p-4" data-id="element-206">
                        <p className="text-center text-xs md:text-sm text-gray-600 mb-1" data-id="element-207">
                          Venit lunar estimat
                        </p>
                        <p className="text-center text-2xl md:text-3xl font-bold text-[#357beb]" data-id="element-208">
                          {monthlyRentalIncome.toLocaleString()} €{' '}
                          <span className="text-base md:text-xl" data-id="element-209">+TVA</span>
                        </p>
                      </div>
                    </div>
                    {/* Rental ROI */}
                    <motion.div className="bg-gradient-to-br from-[#357beb] to-[#2868d9] p-6 md:p-8 rounded-xl mb-6 md:mb-8" initial={{
                  scale: 0.9,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  delay: 0.2
                }} data-id="element-210">
                      <div className="flex items-center justify-center gap-2 md:gap-3 mb-2" data-id="element-211">
                        <PercentIcon className="w-6 h-6 md:w-8 md:h-8 text-white" data-id="element-212" />
                        <p className="text-white text-base md:text-lg font-semibold" data-id="element-213">
                          Randament Anual (ROI)
                        </p>
                      </div>
                      <p className="text-white text-5xl md:text-6xl font-bold text-center" data-id="element-214">
                        <CountUp end={rentalROI} duration={1.5} decimals={1} data-id="element-215" />%
                      </p>
                    </motion.div>
                    {/* Time Period Projections */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-id="element-216">
                      {[{
                    years: 1,
                    income: annualRentalIncome,
                    label: '1 An'
                  }, {
                    years: 3,
                    income: threeYearRentalIncome,
                    label: '3 Ani'
                  }, {
                    years: 10,
                    income: tenYearRentalIncome,
                    label: '10 Ani'
                  }].map((period, index) => <motion.div key={period.years} className="bg-white border-2 border-gray-200 p-5 md:p-6 rounded-xl hover:border-[#357beb] transition-all" initial={{
                    scale: 0.9,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} transition={{
                    delay: 0.3 + index * 0.1
                  }} data-id="element-217">
                          <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4" data-id="element-218">
                            <CalendarIcon className="w-5 h-5 md:w-6 md:h-6 text-[#357beb]" data-id="element-219" />
                            <h4 className="font-bold text-[#132c53] text-base md:text-lg" data-id="element-220">
                              {period.label}
                            </h4>
                          </div>
                          <p className="text-xs md:text-sm text-gray-600 mb-2" data-id="element-221">
                            Profit total din închiriere
                          </p>
                          <p className="text-2xl md:text-3xl font-bold text-[#357beb]" data-id="element-222">
                            <CountUp end={period.income} duration={1.5} separator="," data-id="element-223" />{' '}
                            € <span className="text-xs md:text-sm" data-id="element-224">+TVA</span>
                          </p>
                          <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-gray-200" data-id="element-225">
                            <p className="text-xs text-gray-500" data-id="element-226">
                              ROI cumulat:{' '}
                              <span className="font-bold text-[#132c53]" data-id="element-227">
                                {(period.income / investmentAmount * 100).toFixed(1)}
                                %
                              </span>
                            </p>
                          </div>
                        </motion.div>)}
                    </div>
                  </div>
                </motion.div>}
              {/* Resale Tab */}
              {activeTab === 'resale' && <motion.div key="resale" variants={tabContentVariants} initial="hidden" animate="visible" exit="exit" data-id="element-228">
                  <div className="max-w-4xl mx-auto" data-id="element-229">
                    <h3 className="text-xl md:text-2xl font-bold text-[#132c53] mb-6 md:mb-8 text-center px-4" data-id="element-230">
                      Calculează randamentul din revânzare
                    </h3>
                    {/* Price per SQM Slider */}
                    <div className="bg-gray-50 p-6 md:p-8 rounded-xl mb-6 md:mb-8" data-id="element-231">
                      <label className="block text-center text-base md:text-lg font-bold text-[#132c53] mb-4" data-id="element-232">
                        Preț revânzare (€/mpc)
                      </label>
                      <div className="text-center mb-6" data-id="element-233">
                        <span className="text-4xl md:text-5xl font-bold text-[#357beb]" data-id="element-234">
                          {pricePerSqm.toLocaleString()}
                        </span>
                        <span className="text-xl md:text-2xl text-gray-600 ml-2" data-id="element-235">
                          €
                        </span>
                      </div>
                      <input type="range" min="3900" max="6000" step="50" value={pricePerSqm} onChange={e => setPricePerSqm(Number(e.target.value))} className="w-full h-4 md:h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#357beb] touch-manipulation" style={{
                    WebkitAppearance: 'none'
                  }} data-id="element-236" />
                      <div className="flex justify-between text-xs md:text-sm text-gray-500 mt-2 px-1" data-id="element-237">
                        <span data-id="element-238">3.900 €</span>
                        <span data-id="element-239">6.000 €</span>
                      </div>
                      <div className="mt-4 space-y-2" data-id="element-240">
                        <p className="text-center text-xs md:text-sm text-gray-600" data-id="element-241">
                          Preț + TVA (19%):{' '}
                          <span className="font-bold text-[#357beb]" data-id="element-242">
                            {pricePerSqmWithVAT.toLocaleString()} €/mpc
                          </span>
                        </p>
                        <p className="text-center text-xs md:text-sm text-gray-600" data-id="element-243">
                          Valoare totală revânzare:{' '}
                          <span className="font-bold text-[#357beb]" data-id="element-244">
                            {totalResaleValue.toLocaleString()} €
                          </span>
                        </p>
                      </div>
                    </div>
                    {/* Resale Summary Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mb-6 md:mb-8" data-id="element-245">
                      <motion.div className="bg-gradient-to-br from-green-500 to-green-600 p-6 md:p-8 rounded-xl" initial={{
                    scale: 0.9,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} transition={{
                    delay: 0.2
                  }} data-id="element-246">
                        <div className="flex items-center gap-2 md:gap-3 mb-2" data-id="element-247">
                          <CoinsIcon className="w-6 h-6 md:w-8 md:h-8 text-white" data-id="element-248" />
                          <p className="text-white text-base md:text-lg font-semibold" data-id="element-249">
                            Profit Estimat
                          </p>
                        </div>
                        <p className="text-white text-4xl md:text-5xl font-bold" data-id="element-250">
                          <CountUp end={resaleProfit} duration={1.5} separator="," data-id="element-251" />{' '}
                          €
                        </p>
                      </motion.div>
                      <motion.div className="bg-gradient-to-br from-[#357beb] to-[#2868d9] p-6 md:p-8 rounded-xl" initial={{
                    scale: 0.9,
                    opacity: 0
                  }} animate={{
                    scale: 1,
                    opacity: 1
                  }} transition={{
                    delay: 0.3
                  }} data-id="element-252">
                        <div className="flex items-center gap-2 md:gap-3 mb-2" data-id="element-253">
                          <PercentIcon className="w-6 h-6 md:w-8 md:h-8 text-white" data-id="element-254" />
                          <p className="text-white text-base md:text-lg font-semibold" data-id="element-255">
                            ROI Revânzare
                          </p>
                        </div>
                        <p className="text-white text-4xl md:text-5xl font-bold" data-id="element-256">
                          <CountUp end={resaleROI} duration={1.5} decimals={1} data-id="element-257" />
                          %
                        </p>
                      </motion.div>
                    </div>
                    {/* Breakdown */}
                    <motion.div className="bg-white border-2 border-gray-200 p-6 md:p-8 rounded-xl" initial={{
                  scale: 0.9,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  delay: 0.4
                }} data-id="element-258">
                      <h4 className="font-bold text-[#132c53] text-lg md:text-xl mb-4 md:mb-6 text-center" data-id="element-259">
                        Detalii Tranzacție
                      </h4>
                      <div className="space-y-4" data-id="element-260">
                        <div className="flex items-center justify-between pb-4 border-b border-gray-200" data-id="element-261">
                          <span className="text-gray-600 text-sm md:text-base" data-id="element-262">
                            Investiție inițială:
                          </span>
                          <span className="font-bold text-[#132c53] text-base md:text-lg" data-id="element-263">
                            {investmentAmount.toLocaleString()} €
                          </span>
                        </div>
                        <div className="flex items-center justify-center py-2" data-id="element-264">
                          <ArrowRightIcon className="w-5 h-5 md:w-6 md:h-6 text-[#357beb]" data-id="element-265" />
                        </div>
                        <div className="flex items-center justify-between pb-4 border-b border-gray-200" data-id="element-266">
                          <span className="text-gray-600 text-sm md:text-base" data-id="element-267">
                            Valoare revânzare:
                          </span>
                          <span className="font-bold text-green-600 text-base md:text-lg" data-id="element-268">
                            {totalResaleValue.toLocaleString()} €
                          </span>
                        </div>
                        <div className="flex items-center justify-between pt-4 bg-gradient-to-r from-green-50 to-green-100 p-4 rounded-lg" data-id="element-269">
                          <span className="text-gray-700 font-semibold text-sm md:text-base" data-id="element-270">
                            Profit net:
                          </span>
                          <span className="font-bold text-green-600 text-xl md:text-2xl" data-id="element-271">
                            +{resaleProfit.toLocaleString()} €
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </section>;
}